import { Button, message, Table, Modal, Tooltip, Alert } from 'antd'
import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom';
import Filter from './../../../components/Filter';
import axios from 'axios';
import { useSelector } from 'react-redux';
import useResponsive from '../../../hooks/useResponsive';
import debounce from 'lodash.debounce';

function Patients() {
    const { isResponsiveMode } = useResponsive();
    const userType = useSelector((state) => state.user?.user?.roleDetails?.id);
    const navigate = useNavigate();
    const API_URL = import.meta.env.VITE_API_URL;
    const accessToken = useSelector((state) => state.user.accessToken);
    const pharmacyDetails = useSelector((state) => state.user.pharmacyDetails);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [filterOptions, setFilterOptions] = useState([]);
    const [pharmacies, setPharmacies] = useState([]);
    const [filterDropdowns, setFilterDropdowns] = useState([
        {
            title: "Pharmacy",
            options: [],
        },
        {
            title: "Status",
            options: [],
        }

    ]);
    const [selectedFilters, setSelectedFilters] = useState({ pharmacyId: null, status: null, search: null });
    const [isAddPatientModalVisible, setIsAddPatientModalVisible] = useState(false);
    const [searchPhoneNumber, setSearchPhoneNumber] = useState('');
    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {
        if ([2, 4].includes(userType)) {
            fetchPatients(
                1,
                pagination.pageSize,
                pharmacyDetails?.id,
                selectedFilters.status,
                selectedFilters.search
            );

        }
    }, [pharmacyDetails?.id]);

    // Modify existing useEffect to exclude pharmacyId param
    useEffect(() => {
        if (![2, 4].includes(userType)) {
            fetchPatients(
                1,
                pagination.pageSize,
                selectedFilters.pharmacyId,
                selectedFilters.status,
                selectedFilters.search
            );
        }
    }, [pagination.pageSize, selectedFilters]);
    // Fetch pharmacy dropdowns
    const fetchDropdownData = async () => {
        try {
            let pharmacyList = [];
            let pharmacyListID = [];

            if (userType === 4) {
                // For userType 4, use their pharmacy details and don't show pharmacy filter
                if (pharmacyDetails) {
                    pharmacyList = [pharmacyDetails.name];
                    pharmacyListID = [{ id: pharmacyDetails.id, name: pharmacyDetails.name }];
                }

                // Set filter dropdowns without Pharmacy
                setFilterDropdowns([
                    {
                        title: "Status",
                        options: ["Active", "Inactive"],
                    }
                ]);
            }
            else if (userType === 2) {
                // For userType 2, show pharmacy filter with their branches
                if (pharmacyDetails) {
                    pharmacyList = [pharmacyDetails.name];
                    pharmacyListID = [{ id: pharmacyDetails.id, name: pharmacyDetails.name }];
                }

                setFilterDropdowns([
                    {
                        title: "Status",
                        options: ["Active", "Inactive"],
                    }
                ]);
            }
            else {
                // For other users, show full pharmacy filter
                const pharmacyResponse = await axios.get(`${API_URL}pharmacies/get-pharmacies?limit=1000`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });

                pharmacyList = pharmacyResponse.data.Pharmacies.map(pharmacy => pharmacy.name);
                pharmacyListID = pharmacyResponse.data.Pharmacies.map(pharmacy => ({
                    id: pharmacy.id,
                    name: pharmacy.name
                }));

                setFilterDropdowns([
                    {
                        title: "Pharmacy",
                        options: pharmacyList,
                    },
                    {
                        title: "Status",
                        options: ["Active", "Inactive"],
                    }
                ]);
            }

            setPharmacies(pharmacyListID);

        } catch (error) {
            console.error("Error fetching pharmacies:", error);
            message.error("Failed to load filter data.");
        }
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    // Create a debounced version of fetchPatients
    const debouncedFetchPatients = useCallback(
        debounce((page, pageSize, pharmacyId, active, search) => {
            fetchPatients(page, pageSize, pharmacyId, active, search);
        }, 2000), // 2 seconds delay
        []
    );

    const handleFilterChange = (key, value) => {
        let updatedFilters = { ...selectedFilters };
        let errorMessageShown = false;

        if (key === "Pharmacy") {
            const pharmacyId = value ? pharmacies.find(ph => ph.name === value)?.id : null;
            updatedFilters.pharmacyId = pharmacyId;
        }

        if (key === "Status") {
            const status = value === "Active" ? 1 : value === "Inactive" ? 0 : null;
            updatedFilters.status = status;
        }

        if (key === "search") {
            let search = null;

            if (value && value.trim().length > 0) {
                // Keep the search value as a string to preserve leading zeros
                if (value.length > 4) {
                    search = value.trim();
                    setSearchPhoneNumber(value.trim());
                }
            }

            updatedFilters.search = search;
        }

        if (key === "") {
            fetchPatients();
            updatedFilters = {};
        }

        setSelectedFilters(updatedFilters);

        // Use debounced version for search
        if (key === "search") {
            debouncedFetchPatients(1, pagination.pageSize, updatedFilters.pharmacyId, updatedFilters.status, updatedFilters.search);
        } else {
            fetchPatients(1, pagination.pageSize, updatedFilters.pharmacyId, updatedFilters.status, updatedFilters.search);
        }
    };


    const fetchPatients = async (page = 1, pageSize = 10, pharmacyId = null, active = null, search = null) => {
        setLoading(true);
        try {
            let actualPharmacyId = [2, 4].includes(userType)
                ? pharmacyDetails?.id
                : pharmacyId;

            const queryParams = new URLSearchParams({
                limit: pageSize,
                page: page,
                ...(actualPharmacyId && { pharmacyId: actualPharmacyId }),
                ...(active !== null && { active }),
                ...(search && { search }),
            }).toString();

            const response = await axios.get(`${API_URL}users/get-patients?${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const { Patients, totalCount } = response.data;
            setPatients(Patients);
            setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize,
                total: totalCount,
            }));

            // Show alert if no patients found and there was a search
            if (Patients.length === 0 && search) {
                setShowAlert(true);
            } else {
                setShowAlert(false);
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
            message.destroy();
            message.error("Failed to fetch patients");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPatients(1, pagination.pageSize, selectedFilters.pharmacyId, selectedFilters.status, selectedFilters.search);
    }, [pagination.pageSize, selectedFilters]);

    useEffect(() => {
        fetchPatients(pagination.current, pagination.pageSize, selectedFilters.pharmacyId, selectedFilters.status, selectedFilters.search);
    }, [pagination.current]);


    const handleStatusChange = async (id, isActive) => {
        const status = isActive ? 1 : 0;
        try {
            const response = await axios.patch(`${API_URL}Users/?id=${id}`, { active: status },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            if (response.status === 200) {
                fetchPatients()

            }
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const handleViewProfile = (data) => {
        navigate(`viewPatients/${data.id}`, { state: { data: data } });
    }
    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(`${API_URL}users/?id=${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            if (response.status === 200) {
                fetchPatients()
                message.success("Patient deleted successfully");
                message.destroy();
            }

        } catch (error) {
            console.error("Error deleting patient:", error);
            message.destroy();
            message.error("Failed to delete patient");
        }
    };
    const columns = [
        {
            title: <span>Patient Name</span>,
            dataIndex: 'userDetails',
            key: 'patient_name',
            render: (userDetails) => {
                const firstName = userDetails?.firstName || '';
                const lastName = userDetails?.lastName || '';
                return (firstName || lastName) ? `${firstName} ${lastName}` : '';
            },
        }
        ,
        {
            title: "Phone number",
            dataIndex: "mobileNumber",
            key: "Phone_number",
        },
        {
            title: "Pharmacy",
            dataIndex: "PharmaciesBranches",
            key: "Pharmacy",
            render: (PharmaciesBranches) => {
                if (PharmaciesBranches && PharmaciesBranches.length > 0) {
                    return PharmaciesBranches[0].PharmacyDetails?.name || '';
                }
                return '';
            },
        }
        ,

        {
            title: "Profile",
            dataIndex: "Profile",
            render: (text, row) => (
                <div className="row-actions">
                    <Button
                        style={{ marginRight: 16, borderRadius: 0, borderColor: "#C0D3F9" }}
                        onClick={() => handleViewProfile(row)}
                    >
                        View
                    </Button>
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "active",
            render: (text, row) => (

                <div className="flex">
                    <Button
                        disabled={row.active}
                        style={{
                            borderRadius: 0,
                            border: 0,
                            margin: 0,
                            backgroundColor: row.active ? "#3371EB" : "#F7F8FF",
                            color: row.active ? "#FFFFFF" : "#123288",
                            cursor: row.active ? "not-allowed" : "pointer",
                        }}
                        onClick={() => handleStatusChange(row.id, true, row.mobileNumber)} // Active button click
                    >
                        Active
                    </Button>
                    <Button
                        disabled={!row.active}
                        style={{
                            borderRadius: 0,
                            margin: 0,
                            border: 0,
                            backgroundColor: row.active ? "#F7F8FF" : "#3371EB",
                            color: row.active ? "#123288" : "#FFFFFF",
                            cursor: !row.active ? "not-allowed" : "pointer",
                        }}
                        onClick={() => handleStatusChange(row.id, false)} // Inactive button click
                    >
                        Inactive
                    </Button>
                </div>
            ),
        }
    ];
    if (userType === 1) {
        columns.push({
            title: "Delete",
            key: "delete",
            render: (text, row) => (
                <Button
                    danger
                    style={{ borderRadius: 0, borderColor: "#FF4D4F", backgroundColor: "#FF4D4F", color: "#FFFFFF" }}
                    onClick={() => handleDelete(row.id)}
                >
                    Delete
                </Button>
            ),
        });
    }
    const handleTableChange = (pagination) => {
        setPagination(pagination);
        fetchPatients(pagination.current, pagination.pageSize, selectedFilters.pharmacyId, selectedFilters.status, selectedFilters.search);
    };


    return (
        <>
            <div className='space-y-5'>
                {showAlert && (
                    <Alert
                        message={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>No patient found with this number</span>
                                <Button
                                    type="primary"
                                    onClick={() => navigate('AddPatients', { state: { phoneNumber: searchPhoneNumber } })}
                                    style={{
                                        backgroundColor: '#3371EB',
                                        borderColor: '#3371EB',
                                        marginLeft: '16px'
                                    }}
                                >
                                    Add New Patient
                                </Button>
                            </div>
                        }
                        type="info"
                        showIcon
                        closable
                        onClose={() => setShowAlert(false)}
                        style={{
                            marginBottom: '16px',
                            borderRadius: '4px'
                        }}
                    />
                )}
                {userType == 2 ? (
                    <Filter
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                        dropdowns={filterDropdowns}
                        addItemName='Add Patient'
                        addItemUrl='AddPatients'
                        searchBarPlaceholder='Search Phone Number'
                        filterBy={true}
                        resetFilter={true}
                        onFilterChange={handleFilterChange}
                    />
                ) : (
                    <Filter
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                        dropdowns={filterDropdowns}
                        addItemName='Add Patient'
                        addItemUrl='AddPatients'
                        searchBarPlaceholder='Search Phone Number'
                        filterBy={true}
                        resetFilter={true}
                        onFilterChange={handleFilterChange}
                    />
                )}
                <div className="table-responsive-wrapper responsive-table-container">
                    <Table
                        className="customTable"
                        style={{ backgroundColor: '#FFFFFF', padding: "10px" }}
                        columns={columns}
                        dataSource={patients}
                        rowKey="id"
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                    />
                </div>
            </div>
        </>
    );
}

export default Patients;
